/**
 * `createDocContainer` (W1-03 core-model-ops; ARCHITECTURE.md §5.1-5.3): the record Maps, the derived indexes and
 * `apply`. Used by the main-thread DocStore and by every worker replica (same code, so replicas stay identical).
 *
 * The container copies the Maps of the document it is created from (that document is never mutated) and from then
 * on owns them: `doc` is a live view (tables change in place on apply; singletons and order lists are replaced;
 * records are immutable values; a record holding -0 is replaced by a copy holding 0). Index results are snapshots:
 * a later apply never mutates an array or map that was handed out (it copies first), so they can be compared by
 * identity.
 */

import { Beats } from '../time/beat';
import { createTempoMap } from '../time/tempo';
import type { TempoMap } from '../time/types';
import type { ChartDoc, DocIndexes } from '../model/types';
import type { CreateDocContainer, DocContainer, Op } from '../ops/types';
import { applyOp } from './apply';
import { DocIndexStore } from './indexes';
import { canonicalizeNegativeZero, createBaseState, type DocState } from './state';

/** Internals shared with the TxDraft overlay (not part of the public contract). */
export interface ContainerCore {
  readonly state: DocState;
  readonly idx: DocIndexStore;
  /** Incremented on every op that touches the BPM table. */
  bpmVersion: number;
  tempoCache: { readonly version: number; readonly map: TempoMap } | null;
}

const CORES = new WeakMap<DocContainer, ContainerCore>();

/** The container's internals when it was created by `createDocContainer`. */
export function coreOf(c: DocContainer): ContainerCore | undefined {
  return CORES.get(c);
}

/** Phira-exact tempo map of a document view (BPM points in beat order, raw imported triples when present). */
export function buildTempoMap(doc: ChartDoc, idx: Pick<DocIndexes, 'bpmSorted'>): TempoMap {
  const list = idx.bpmSorted().map((id) => {
    const p = doc.bpm.get(id);
    if (p === undefined) throw new Error(`tempo: BPM point ${String(id)} is indexed but missing`);
    return { bpm: p.bpm, startTime: p.src?.rawStart ?? Beats.toTriple(p.t) };
  });
  if (list.length === 0) throw new Error('tempo: the document has no BPM point');
  return createTempoMap(list);
}

/** Tempo map of a container's current state, cached until the BPM table changes. */
export function containerTempo(core: ContainerCore): TempoMap {
  const cached = core.tempoCache;
  if (cached !== null && cached.version === core.bpmVersion) return cached.map;
  const map = buildTempoMap(core.state, core.idx);
  core.tempoCache = { version: core.bpmVersion, map };
  return map;
}

export function touchesBpm(op: Op): boolean {
  return (op.o === 'ins' || op.o === 'del' || op.o === 'set') && op.tbl === 'bpm';
}

/** A `DocContainer` with an additional cached tempo map (`tempo()`), rebuilt when the BPM table changes. */
export interface DocContainerWithTempo extends DocContainer {
  tempo(): TempoMap;
}

export const createDocContainer: CreateDocContainer = (doc) => createContainer(doc);

/** Same as `createDocContainer`, typed with the extra `tempo()` accessor. */
export function createContainer(doc: ChartDoc): DocContainerWithTempo {
  const state = createBaseState(doc);
  // A document never holds -0 (core/ops/equal.ts); a lifted file may have written `-0.0`.
  canonicalizeNegativeZero(state);
  const core: ContainerCore = { state, idx: DocIndexStore.build(state), bpmVersion: 0, tempoCache: null };
  const apply = (op: Op): void => {
    applyOp(core.state, core.idx, op);
    if (touchesBpm(op)) core.bpmVersion++;
  };
  const container: DocContainerWithTempo = {
    doc: state,
    idx: core.idx,
    apply,
    applyAll: (ops) => {
      for (const op of ops) apply(op);
    },
    tempo: () => containerTempo(core),
  };
  CORES.set(container, core);
  return container;
}
